import { useState, useEffect } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { FileInput, Plus, Camera, Truck, Calendar, Search } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MaterialEntryLogPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [dateFilter, setDateFilter] = useState("");
  const [form, setForm] = useState({
    material: "", supplier: "", supplier_area: "", vehicle_number: "",
    quantity_mt: "", rate: "", challan_number: "", notes: "", unit: "Unit 1"
  });
  const [challanFile, setChallanFile] = useState(null);

  const fetchEntries = async () => {
    try {
      const params = dateFilter ? `?date=${dateFilter}` : "";
      const res = await authFetch(`${API}/factory/material-entries${params}`);
      const data = await res.json();
      if (data.success) setEntries(data.data || []);
    } catch { toast.error("Failed to load entries"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchEntries(); }, [dateFilter]);

  const uploadChallan = async () => {
    if (!challanFile) return "";
    const formData = new FormData();
    formData.append("file", challanFile);
    const res = await authFetch(`${API}/upload?category=challan-photos`, { method: "POST", body: formData });
    const data = await res.json();
    return data.success ? data.data.storage_path : "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      let challanPath = "";
      if (challanFile) challanPath = await uploadChallan();
      
      const body = {
        ...form,
        quantity_mt: parseFloat(form.quantity_mt),
        rate: parseFloat(form.rate),
        challan_photo: challanPath,
      };
      const res = await authFetch(`${API}/factory/material-entries`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Material entry logged");
        setShowAdd(false);
        setForm({ material: "", supplier: "", supplier_area: "", vehicle_number: "", quantity_mt: "", rate: "", challan_number: "", notes: "", unit: "Unit 1" });
        setChallanFile(null);
        fetchEntries();
      } else toast.error(data.detail || "Failed");
    } catch { toast.error("Network error"); }
    finally { setCreating(false); }
  };

  return (
    <div className="space-y-6" data-testid="material-entry-log-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Sora']">Material Entry Log</h1>
          <p className="text-emerald-600 text-sm mt-1">Track incoming raw material arrivals</p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-material-entry-btn">
          <Plus className="w-4 h-4 mr-2" /> Log Entry
        </Button>
      </div>

      <div className="flex gap-3">
        <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="w-48 bg-[#1C2B2B] border-emerald-900/50 text-white" data-testid="date-filter" />
      </div>

      <Card className="bg-[#1C2B2B] border-emerald-900/30 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="material-entries-table">
            <thead>
              <tr className="border-b border-emerald-900/30">
                <th className="text-left p-3 text-emerald-600 text-xs font-medium">Date/Time</th>
                <th className="text-left p-3 text-emerald-600 text-xs font-medium">Material</th>
                <th className="text-left p-3 text-emerald-600 text-xs font-medium">Supplier</th>
                <th className="text-left p-3 text-emerald-600 text-xs font-medium">Area</th>
                <th className="text-left p-3 text-emerald-600 text-xs font-medium">Vehicle</th>
                <th className="text-right p-3 text-emerald-600 text-xs font-medium">Qty (MT)</th>
                <th className="text-right p-3 text-emerald-600 text-xs font-medium">Rate</th>
                <th className="text-left p-3 text-emerald-600 text-xs font-medium">Challan</th>
                <th className="text-left p-3 text-emerald-600 text-xs font-medium">Unit</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-8"><div className="w-6 h-6 spinner mx-auto" /></td></tr>
              ) : entries.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-slate-500">No entries yet. Click "Log Entry" to add one.</td></tr>
              ) : entries.map((e, i) => (
                <tr key={e.entry_id || i} className="border-b border-emerald-900/20 hover:bg-emerald-900/10" data-testid={`entry-row-${i}`}>
                  <td className="p-3 text-slate-300 text-xs whitespace-nowrap">{new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</td>
                  <td className="p-3 text-white font-medium">{e.material}</td>
                  <td className="p-3 text-slate-300">{e.supplier}</td>
                  <td className="p-3 text-slate-400">{e.supplier_area}</td>
                  <td className="p-3 text-slate-300">{e.vehicle_number}</td>
                  <td className="p-3 text-right text-emerald-400 font-mono">{e.quantity_mt}</td>
                  <td className="p-3 text-right text-slate-300 font-mono">{e.rate}</td>
                  <td className="p-3">{e.challan_photo ? <Badge className="bg-emerald-900/30 text-emerald-400 text-[10px]">Uploaded</Badge> : <span className="text-slate-500 text-xs">-</span>}</td>
                  <td className="p-3"><Badge variant="outline" className="text-[10px] border-emerald-700 text-emerald-400">{e.unit || "Unit 1"}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-lg bg-[#1A2332] border-emerald-900/50 text-white">
          <DialogHeader>
            <DialogTitle className="text-white font-['Sora']">Log Material Entry</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-emerald-600 font-medium">Material</label>
                <Input value={form.material} onChange={(e) => setForm({...form, material: e.target.value})} required className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" placeholder="e.g. Limestone, Fly Ash" data-testid="entry-material" />
              </div>
              <div>
                <label className="text-xs text-emerald-600 font-medium">Unit</label>
                <Select value={form.unit} onValueChange={(v) => setForm({...form, unit: v})}>
                  <SelectTrigger className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Unit 1">Unit 1</SelectItem><SelectItem value="Unit 2">Unit 2</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-emerald-600 font-medium">Supplier</label>
                <Input value={form.supplier} onChange={(e) => setForm({...form, supplier: e.target.value})} required className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" data-testid="entry-supplier" />
              </div>
              <div>
                <label className="text-xs text-emerald-600 font-medium">Supplier Area</label>
                <Input value={form.supplier_area} onChange={(e) => setForm({...form, supplier_area: e.target.value})} className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" data-testid="entry-area" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-emerald-600 font-medium">Quantity (MT)</label>
                <Input type="number" step="0.01" value={form.quantity_mt} onChange={(e) => setForm({...form, quantity_mt: e.target.value})} required className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" data-testid="entry-qty" />
              </div>
              <div>
                <label className="text-xs text-emerald-600 font-medium">Rate (per MT)</label>
                <Input type="number" step="0.01" value={form.rate} onChange={(e) => setForm({...form, rate: e.target.value})} required className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" data-testid="entry-rate" />
              </div>
              <div>
                <label className="text-xs text-emerald-600 font-medium">Vehicle No.</label>
                <Input value={form.vehicle_number} onChange={(e) => setForm({...form, vehicle_number: e.target.value})} className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" data-testid="entry-vehicle" />
              </div>
            </div>
            <div>
              <label className="text-xs text-emerald-600 font-medium">Challan Photo</label>
              <label className="flex items-center gap-2 mt-1 px-3 py-2 border border-dashed border-emerald-700 rounded-md cursor-pointer hover:border-emerald-500 bg-[#1C2B2B]">
                <Camera className="w-4 h-4 text-emerald-500" />
                <span className="text-sm text-slate-400">{challanFile ? challanFile.name : "Click to upload challan photo"}</span>
                <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => setChallanFile(e.target.files?.[0])} data-testid="entry-challan-upload" />
              </label>
            </div>
            <div>
              <label className="text-xs text-emerald-600 font-medium">Notes</label>
              <Input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="bg-[#1C2B2B] border-emerald-900/50 text-white mt-1" placeholder="Optional notes" data-testid="entry-notes" />
            </div>
            <Button type="submit" disabled={creating} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-entry-btn">
              {creating ? "Saving..." : "Log Entry"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaterialEntryLogPage;
